import { useNavigate } from 'react-router';
import Button from '@/components/UI/Button';

/**
 * Unauthorized Page
 *
 * Displayed when a user attempts to access a route or resource they don't have
 * permission to access. This can happen when:
 * - A tenant user tries to access system admin routes (/os/*)
 * - A system admin tries to access tenant-only routes (/app/*)
 * - A tenant user without required privileges tries to access protected features
 *
 * Provides navigation back to the appropriate dashboard based on user type.
 */
export default function Unauthorized() {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    // Navigate to root, which will redirect based on user type via RootRedirect
    navigate('/');
  };

  return (
    <div className='flex items-center justify-center min-h-screen bg-gray-50 px-4'>
      <div className='text-center max-w-md'>
        {/* Error Icon */}
        <div className='mb-6'>
          <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100'>
            <svg className='w-8 h-8 text-red-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4v2m0 0v2m0-6v-2m0 0v-2m0 2H9m3 0h3' />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className='text-3xl font-bold text-gray-900 mb-2'>Access Denied</h1>

        {/* Description */}
        <p className='text-gray-600 mb-6'>
          You don't have permission to access this resource. If you believe this is an error, please contact your administrator.
        </p>

        {/* Error Code */}
        <div className='bg-gray-100 rounded-lg p-4 mb-6'>
          <p className='text-sm text-gray-500'>Error Code: 403 Forbidden</p>
        </div>

        {/* Action Buttons */}
        <div className='flex gap-3'>
          <Button onClick={handleGoBack} label='Go Back' btnStyle='secondary' customClassName='flex-1' />
          <Button onClick={handleGoHome} label='Go to Dashboard' btnStyle='primary' customClassName='flex-1' />
        </div>
      </div>
    </div>
  );
}
